/**
 * SSR request/HTTP diagnostics for the 504 investigation.
 *
 * Why this exists: Angular resolves `commonEngine.render()` only once every in-flight
 * HttpClient request has settled, and this app sets no HTTP timeout anywhere. A single
 * call that never completes therefore hangs its whole render forever, holding an open
 * connection and a full SSR context. Enough of those and the process stops answering
 * anything at all — including Express routes that never touch Angular, which is why a
 * 504 on `/favicon.ico` was the symptom rather than a clue.
 *
 * From outside the box we can see that a render never finished, but not what it was
 * waiting on. That is the gap this closes: it records every in-flight render and HTTP
 * call, and when a render outlives its threshold it prints the exact list of requests
 * still outstanding.
 *
 * Disabled unless `DIAG=1`, so it is safe to ship and costs nothing until switched on.
 *
 * Env knobs (all optional):
 *   DIAG=1                     enable
 *   DIAG_SLOW_HTTP_MS=5000     warn about any HTTP call that took at least this long
 *   DIAG_SLOW_RENDER_MS=10000  report a render still running after this long
 *   DIAG_SNAPSHOT_MS=5000      how often to print the in-flight snapshot
 */

/* This module is imported by the interceptor, which runs on the browser too, where
   `process` does not exist — so every access to it has to be guarded. */
const env: Record<string, string | undefined> =
    (typeof process !== 'undefined' && process?.env) ? process.env : {};

export const DIAG_ENABLED: boolean = env['DIAG'] === '1';

const
    SLOW_HTTP_MS: number = Number(env['DIAG_SLOW_HTTP_MS'] ?? 5000),
    SLOW_RENDER_MS: number = Number(env['DIAG_SLOW_RENDER_MS'] ?? 10000),
    SNAPSHOT_MS: number = Number(env['DIAG_SNAPSHOT_MS'] ?? 5000);

interface Tracked {
    label: string;
    startedAt: number;
    /* So a stuck render is reported once rather than on every snapshot tick. */
    reported: boolean;
}

let sequence: number = 0;

const
    httpInFlight: Map<number, Tracked> = new Map<number, Tracked>(),
    renderInFlight: Map<number, Tracked> = new Map<number, Tracked>();

function ageOf(entry: Tracked): number {
    return Date.now() - entry.startedAt;
}

/* Oldest first — the oldest entry is the one that is stuck. */
function describe(entries: Map<number, Tracked>): string[] {
    return Array.from(entries.values())
        .sort((a, b) => a.startedAt - b.startedAt)
        .map(entry => `${ageOf(entry)}ms  ${entry.label}`);
}

function track(entries: Map<number, Tracked>, label: string, slowMs: number, kind: string): () => void {
    if (!DIAG_ENABLED) {
        return () => { };
    }

    const id: number = ++sequence;
    entries.set(id, { label, startedAt: Date.now(), reported: false });

    /* Never called when the underlying work hangs — that is the point: the entry stays
       in the map and the next snapshot names it. */
    return () => {
        const entry = entries.get(id);

        if (!entry) {
            return;
        }

        entries.delete(id);

        if (ageOf(entry) >= slowMs) {
            console.warn(`[DIAG] slow ${kind}  ${ageOf(entry)}ms  ${entry.label}`);
        }
    };
}

/** Call when an HTTP request starts; call the returned fn when it settles. */
export function diagTrackHttp(label: string): () => void {
    return track(httpInFlight, label, SLOW_HTTP_MS, 'http');
}

/** Call when an SSR render starts; call the returned fn when it settles. */
export function diagTrackRender(label: string): () => void {
    return track(renderInFlight, label, SLOW_RENDER_MS, 'render');
}

export function diagSnapshot(): Record<string, unknown> {
    const memory = (typeof process !== 'undefined' && process?.memoryUsage) ? process.memoryUsage() : null;

    return {
        rendersInFlight: renderInFlight.size,
        httpInFlight: httpInFlight.size,
        rssMb: memory ? Math.round(memory.rss / 1048576) : null,
        heapUsedMb: memory ? Math.round(memory.heapUsed / 1048576) : null,
        oldestRenders: describe(renderInFlight).slice(0, 5),
        oldestHttp: describe(httpInFlight).slice(0, 10),
    };
}

/**
 * Periodic snapshot, plus a one-off report per render that outlives SLOW_RENDER_MS
 * listing the HTTP calls it is still waiting on. Server-side only.
 */
export function startDiagSnapshots(): void {
    if (!DIAG_ENABLED) {
        return;
    }

    console.log(`[DIAG] enabled — snapshot every ${SNAPSHOT_MS}ms, slow http ${SLOW_HTTP_MS}ms, stuck render ${SLOW_RENDER_MS}ms`);

    const timer: any = setInterval(() => {
        renderInFlight.forEach(entry => {
            if (entry.reported || ageOf(entry) < SLOW_RENDER_MS) {
                return;
            }

            entry.reported = true;
            console.error(`[DIAG] STUCK RENDER  ${ageOf(entry)}ms  ${entry.label}`);
            console.error(`[DIAG]   still waiting on ${httpInFlight.size} http call(s):`);
            describe(httpInFlight).forEach(line => console.error(`[DIAG]     ${line}`));
        });

        if (renderInFlight.size > 0 || httpInFlight.size > 0) {
            console.log(`[DIAG] ${JSON.stringify(diagSnapshot())}`);
        }
    }, SNAPSHOT_MS);

    /* Do not hold the process open just for diagnostics. */
    timer?.unref?.();
}

/**
 * Log unhandled rejections and uncaught exceptions instead of dying silently.
 *
 * Registering these handlers also SUPPRESSES Node's default behaviour of terminating the
 * process on an unhandled rejection, which is deliberate: the current failure mode is the
 * process disappearing with no trace, and a rejected promise does not corrupt state the
 * way a mid-write crash would. If you would rather keep fail-fast semantics, set
 * DIAG_EXIT_ON_FATAL=1 and it will log the stack and then exit as before.
 */
export function installFatalHandlers(): void {
    if (typeof process === 'undefined' || !process?.on) {
        return;
    }

    const exitOnFatal: boolean = env['DIAG_EXIT_ON_FATAL'] === '1';

    const report = (kind: string, error: any): void => {
        console.error(`[FATAL] ${kind}:`, error?.stack || error);
        console.error(`[FATAL] in-flight at time of failure: ${JSON.stringify(diagSnapshot())}`);

        if (exitOnFatal) {
            process.exit(1);
        }
    };

    process.on('unhandledRejection', reason => report('unhandledRejection', reason));
    process.on('uncaughtException', error => report('uncaughtException', error));
}
